import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signInWithPopup, GoogleAuthProvider, sendPasswordResetEmail } from 'firebase/auth'; // Add Google imports
import { doc, getDoc, setDoc } from 'firebase/firestore'; // Add setDoc for storing user data
import '../styles/user.css';
import '../styles/auth.css';
import '../styles/buttons.css';
import logger from '../utils/logger';


function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState(null); // null | 'success' | 'error'
  const [resetError, setResetError] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();

  // Function to check if the user has a display name
  const checkDisplayName = async (user) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.displayName && userData.displayName.trim() !== ''; // Return true if displayName exists and is non-empty
      }
      return false; // If the document doesn't exist, assume no display name
    } catch (err) {
      logger.error(err, { page: 'Login', action: 'checkDisplayName' });
      return false; // On error, assume no display name to avoid blocking the user
    }
  };

  // Redirect authenticated users based on email verification and display name
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        if (user.emailVerified) {
          const hasDisplayName = await checkDisplayName(user);
          if (hasDisplayName) {
            navigate('/user'); // Redirect to /user if display name is set
          } else {
            navigate('/account'); // Redirect to /account to set display name
          }
        } else {
          navigate('/verify-email');
        }
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Handle email/password login
  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      if (user.emailVerified) {
        const hasDisplayName = await checkDisplayName(user);
        if (hasDisplayName) {
          navigate('/user'); // Redirect to /user if display name is set
        } else {
          navigate('/account'); // Redirect to /account to set display name
        }
      } else {
        navigate('/verify-email');
      }
    } catch (error) {
      setError('Failed to login. Please check your email and password.');
    }
  };

// Handle Google Sign-In
const handleGoogleSignIn = async () => {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      // Returning user — only sync email in case it changed; never overwrite
      // name fields the user may have edited in-app.
      await setDoc(userDocRef, { email: user.email }, { merge: true });
    } else {
      // First-time Google sign-in — write the full profile.
      let firstName = '';
      let lastName = '';
      if (user.displayName) {
        const nameParts = user.displayName.trim().split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      }
      await setDoc(userDocRef, {
        userId: user.uid,
        firstName,
        lastName,
        displayName: '',
        email: user.email,
        createdAt: new Date().toISOString(),
      });
      // Write default activity preferences for first-time Google users
      const prefsRef = doc(db, 'users', user.uid, 'preferences', 'activities');
      const prefsSnap = await getDoc(prefsRef);
      if (!prefsSnap.exists()) {
        await setDoc(prefsRef, {
          activities: [
            'Walking', 'Running', 'Push Ups', 'Squats',
            'Jumping Jacks', 'Bicep Curls', 'Bench Press',
            'Sit Ups', 'Lunges', 'Plank'
          ]
        });
      }
    }

    // Check if the user has a display name after signing in
    const hasDisplayName = await checkDisplayName(user);
    if (hasDisplayName) {
      navigate('/user'); // Redirect to /user if display name is set
    } else {
      navigate('/account'); // Redirect to /account to set display name
    }
  } catch (err) {
    logger.error(err, { page: 'Login', action: 'googleSignIn' });
    setError('Failed to sign in with Google. Please try again.');
  }
};

  const handleForgotPassword = () => {
    setShowForgotPassword(true);
    setResetEmail('');
    setResetStatus(null);
    setResetError(null);
  };

  const handleCancelReset = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetStatus(null);
    setResetError(null);
  };

  const handleSendResetEmail = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError(null);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetStatus('success');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError('No account found with that email address.');
      } else if (err.code === 'auth/invalid-email') {
        setResetError('Please enter a valid email address.');
      } else {
        setResetError('Failed to send reset email. Please try again.');
      }
      setResetStatus('error');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="login-container">
        {showForgotPassword ? (
          <>
            <h3>Reset Password</h3>
            {resetStatus === 'success' ? (
              <p className="info-message">
                Check your email for a password reset link.
              </p>
            ) : (
              <>
                {resetError && <p className="error-message">{resetError}</p>}
                <form onSubmit={handleSendResetEmail}>
                  <div className="form-row password-row">
                    <div className="input-wrapper">
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                  </div>
                  <button type="submit" className="btn-primary" disabled={resetLoading}>
                    {resetLoading ? 'Sending...' : 'Send Reset Email'}
                  </button>
                </form>
              </>
            )}
            <p>
              <span className="forgot-password-link" onClick={handleCancelReset}>
                ← Back to Login
              </span>
            </p>
          </>
        ) : (
          <>
            <h3>Login</h3>
            {error && <p className="error-message">{error}</p>}
            <form onSubmit={handleLogin}>
              <div className="form-row">
                <div className="input-wrapper">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>
              <div className="form-row password-row">
                <div className="input-wrapper">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>
              </div>
              <button type="submit" className="btn-primary">Login</button>
            </form>
            <div className="google-signin-container">
              <button onClick={handleGoogleSignIn} className="gsi-material-button">
                <div className="gsi-material-button-content-wrapper">
                  <div className="gsi-material-button-icon">
                    <img src="https://developers.google.com/identity/images/g-logo.png" alt="Google logo" style={{ width: '20px', height: '20px' }} />
                  </div>
                  <span className="gsi-material-button-contents">Sign in with Google</span>
                </div>
              </button>
            </div>
            <p>
              <span className="forgot-password-link" onClick={handleForgotPassword}>
                Forgot Password?
              </span>
            </p>
            <p>
              Don't have an account?{' '}
              <Link to="/signup" className="signup-link">
                Sign Up
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;